'use client';

import React, { useState, useEffect } from 'react';
import { Folder } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { FileManager } from '@/components/files/file-manager';
import { useIsAccountant } from '@/hooks/use-accountant';
import { useBusiness } from '@/contexts/business-context';
import { leaveBreadcrumb } from '@/lib/bugsnag';

interface BusinessFileManagerProps {
  accountId?: string;
  className?: string;
}

export function BusinessFileManager({ accountId, className }: BusinessFileManagerProps) {
  const [showFileManager, setShowFileManager] = useState(false);
  
  // Check if user is an accountant
  const { data: isAccountant, isLoading: isLoadingAccountant } = useIsAccountant();
  
  // Get active business from context - this is the selected business from the dropdown
  // This works for both regular users (multi-business) and accountants
  const { activeBusiness, isLoadingActive: isLoadingActiveBusiness } = useBusiness();
  
  // Always use the active business from context - it updates when user switches businesses
  const business = activeBusiness;
  const isLoading = isLoadingAccountant || isLoadingActiveBusiness;

  // Track when files are disabled due to no business (setup required)
  useEffect(() => {
    if (!isLoading && !business && !isAccountant) {
      leaveBreadcrumb('File manager disabled - setup required', {
        isAccountant: !!isAccountant,
        hasActiveBusiness: !!business,
      }, 'state');
    }
  }, [isLoading, business, isAccountant]);

  if (isLoading) {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (!business) {
    // If there's no business yet, show a disabled placeholder
    return (
      <div className={className}>
        <SidebarMenuButton
          className="hover:bg-orange-50/30 opacity-50 cursor-not-allowed"
          disabled
        >
          <Folder className="h-4 w-4 mr-1 text-gray-400" />
          <span className="truncate text-xs text-gray-500">
            {isAccountant ? 'Manage Files (Select a business)' : 'Manage Files (Setup required)'}
          </span>
        </SidebarMenuButton>
      </div>
    );
  }

  return (
    <>
      <div className={className}>
        <SidebarMenuButton
          className="hover:bg-orange-50/30"
          onClick={() => setShowFileManager(true)}
        >
          <Folder className="h-4 w-4 mr-1" style={{ color: showFileManager ? '#ff6700' : 'inherit' }} />
          <span className="flex items-center justify-between w-full">
            Manage Files
            {showFileManager && (
              <span 
                className="text-xs font-medium"
                style={{ color: '#ff6700' }}
              >
                ●
              </span>
            )}
          </span>
        </SidebarMenuButton>
      </div>

      {/* File Manager Dialog */}
      <Dialog open={showFileManager} onOpenChange={setShowFileManager}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Business Files - {business.name}</DialogTitle>
          </DialogHeader>
          <div className="h-[600px] overflow-hidden">
            <FileManager businessId={business.id} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
